import { useEffect, useMemo, useState } from 'react';
import {
  convertData,
  convertIntervalToSecond,
  createBucketDateFormatter,
  createBucketLookup,
  defaultAE,
  defaultFlowmeter,
  defaultGPS,
  defaultRPM,
  convertShortAELabel,
  generateTimestampData,
  getBucketFuelCons,
  latToDMS,
  longToDMS,
  toHHMMSS,
  toKM,
  toKnot,
  toMiles,
  toNM,
  uniqueByLabel,
} from './helperFunction';
import { getStatusEngine } from '../../../helpers/vessel';
import { generateReportLogColumns, resolveSummaryColumns } from './tableColumn';
import useGroup from '../../../zustand/useGroup';
import useAppliedReportFilter from '../../../hooks/useAppliedReportFilter';
import useReportLog from '../../../hooks/useReportLog';
import useReportSummaryData from '../../../hooks/useReportSummaryData';
import { Divider, Dropdown, Grid, Select, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  CaretDownOutlined,
  FileTextOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import React from 'react';
import TableConfiguration from './tableConfiguration';
import ReportSummary from './summary';
import { EReportDataType, IAE, IOperationMode } from '../types/report-log.type';
import ImportDataReport from './importDataReport';
import ExportDataReport from './exportDataReport';
import usePermission from '../../../hooks/usePermission';
import styled from 'styled-components';
import {
  cruiseUnitOptions,
  distanceUnitOptions,
  literUnitOptions,
  speedUnitOptions,
} from '../../../helpers/unit';
import { useVT } from 'virtualizedtableforantd4';
import { useAuthUser } from 'react-auth-kit';
import useMasterData from '../../../hooks/useMasterData';
import { httpRequest } from '../../../helpers/api';
import { IHttpResponse, IPayloadPagination } from '@qlibs/react-components';
import { OperationModeProperties } from '../../operationMode/types/operationMode.type';
import { EAssetType } from '../../asset/types/asset.type';

const windowHeight = window.innerHeight;
const mobileStickyTableOffset = 60;
const getStickyTableContainer = () =>
  document.getElementById('scroll-content') || window;
export interface RowData {
  [key: string]: string | number | null;
}

interface TableReportLogProps {
  isReportSchedule?: boolean;
}

dayjs.extend(utc);

const TableReportLog: React.FC<TableReportLogProps> = ({
  isReportSchedule,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();
  const { filter, asset, timezone, devices } = useAppliedReportFilter();
  const auth = useAuthUser();
  const { isOperationMode } = useMasterData();
  const user = auth();
  const {
    setChecklistColumn,
    checklistColumn,
    loadingLog: loading,
    logs,
    setSpeedUnit,
    setDistanceUnit,
    distanceUnit,
    setLiterUnit,
    literUnit,
    speedUnit,
    cruiseUnit,
    setCruiseUnit,
  } = useReportLog();
  const [columns, setColumns] = useState<any>([]);
  const [vt] = useVT(() => ({ scroll: { x: 700, y: windowHeight - 300 } }), []);
  const [operationModes, setOperationModes] = useState<
    OperationModeProperties[]
  >([]);

  const { summary: summaryData, gpsSummary } = useReportSummaryData({
    startAt: filter.startAt,
    endAt: filter.endAt,
    interval: filter.interval,
    timezone,
  });

  const availableSummaryColumns = useMemo(() => {
    return resolveSummaryColumns(asset.devices || []);
  }, [asset.devices]);

  const aeFuelRows = useMemo(
    () => generateMobileAERows(summaryData, 'fuelCons'),
    [summaryData],
  );

  const aeRunningRows = useMemo(
    () => generateMobileAERows(summaryData, 'runningTime'),
    [summaryData],
  );

  const renderMobileUnitSelect = (
    value: string,
    onChange: (value: string) => void,
    options: { label: string; value: string }[],
  ) => (
    <MobileSummarySelect
      size='small'
      value={value}
      onChange={(selectedValue) => onChange(String(selectedValue))}
      options={options}
      popupMatchSelectWidth={false}
    />
  );

  useEffect(() => {
    if (asset && asset.devices && asset.devices.length) {
      const res = generateReportLogColumns(
        devices,
        asset.assetType,
        isOperationMode,
      ).map((item: any) => {
        if (item.children) {
          return {
            ...item,
            hidden: false,
            children: item.children.map((el: any) => {
              return {
                ...el,
                hidden: false,
              };
            }),
          };
        } else {
          return {
            ...item,
            hidden: false,
          };
        }
      });

      fetchOperationMode();
      setColumns(res);
    }
  }, [asset, devices, isOperationMode]);

  const fetchOperationMode = async () => {
    try {
      const res = await httpRequest.get<
        IHttpResponse<IPayloadPagination<OperationModeProperties>>
      >('/operation-mode', {
        params: {
          filterCompanyIds: asset.companyId,
        },
      });

      if (
        res &&
        res.data &&
        res.data.payload &&
        res.data.payload.results &&
        res.data.payload.results.length
      ) {
        setOperationModes(res.data.payload.results);
      }
    } catch (error) {
      console.error('Failed to fetch operation modes', error);
    }
  };

  const handleChangeUnit = (e: any, unitType: string) => {
    if (unitType === 'speed') {
      setSpeedUnit(e.key);
    } else if (unitType === 'distance') {
      setDistanceUnit(e.key);
    } else if (unitType === 'liter') {
      setLiterUnit(e.key);
    }
  };

  useEffect(() => {
    if (columns && columns.length) {
      const hasSavedSettings =
        user?.userId &&
        localStorage.getItem(`report-column-settings-${user.userId}`);

      if (!hasSavedSettings) {
        const checklist: string[] = [];

        columns.forEach((item: any) => {
          if (item.children) {
            checklist.push(item.key);
            item.children.forEach((child: any) => {
              checklist.push(child.key);
            });
          } else {
            checklist.push(item.key);
          }
        });

        setChecklistColumn(checklist);
      }
    }
  }, [columns, user?.userId]);

  const columnsFiltered = useMemo(() => {
    const res = columns
      .map((item: any) => {
        if (item.children && item.children.length > 0) {
          const filteredChildren = item.children.filter((child: any) =>
            checklistColumn.includes(child.key),
          );

          if (
            checklistColumn.includes(item.key) ||
            filteredChildren.length > 0
          ) {
            return {
              ...item,
              children:
                filteredChildren.length > 0 ? filteredChildren : undefined,
            };
          }

          return null;
        }

        if (checklistColumn.includes(item.key)) {
          return item;
        }

        return null;
      })
      .filter(Boolean)
      .map((item: any) => {
        if (item.children && item.children.length) {
          return {
            ...item,
            children: item.children.map((child: any) => {
              const titleLower = child.title.toLowerCase();

              if (titleLower === 'speed') {
                return {
                  ...child,
                  title: () => (
                    <div>
                      <span>{child.title}</span>
                      <Dropdown
                        menu={{
                          items: speedUnitOptions.map((i) => ({
                            key: i.value,
                            label: i.label,
                          })),
                          onClick: (e) => handleChangeUnit(e, 'speed'),
                        }}
                      >
                        <TitleChild>
                          <Unit>({speedUnit})</Unit>
                          <CaretDownOutlined style={{ opacity: 0.5 }} />
                        </TitleChild>
                      </Dropdown>
                    </div>
                  ),
                };
              } else if (titleLower === 'distance') {
                return {
                  ...child,
                  title: () => (
                    <div>
                      <span>{child.title}</span>
                      <Dropdown
                        menu={{
                          items: distanceUnitOptions.map((i) => ({
                            key: i.value,
                            label: i.label,
                          })),
                          onClick: (e) => handleChangeUnit(e, 'distance'),
                        }}
                      >
                        <TitleChild>
                          <Unit>({distanceUnit})</Unit>
                          <CaretDownOutlined style={{ opacity: 0.5 }} />
                        </TitleChild>
                      </Dropdown>
                    </div>
                  ),
                };
              } else if (titleLower === 'in flow') {
                return {
                  ...child,
                  title: () => (
                    <div>
                      <span>{child.title}</span>
                      <Dropdown
                        menu={{
                          items: literUnitOptions.map((i) => ({
                            key: i.value,
                            label: i.label,
                          })),
                          onClick: (e) => handleChangeUnit(e, 'liter'),
                        }}
                      >
                        <TitleChild>
                          <Unit>({literUnit})</Unit>
                          <CaretDownOutlined style={{ opacity: 0.5 }} />
                        </TitleChild>
                      </Dropdown>
                    </div>
                  ),
                };
              }

              return child;
            }),
          };
        }

        return item;
      });
    return res;
  }, [columns, checklistColumn, speedUnit, literUnit, distanceUnit]);

  const showTemp = (
    data: any,
    dataType?: EReportDataType | null | undefined,
  ): React.ReactNode => {
    let color = 'black';
    return data;
    if (
      dataType &&
      dataType === EReportDataType.temp &&
      isUserHasPermission(['IMPORT_DATA_LOG' + '.CREATE'])
    ) {
      color = '#195ae7';
    }

    return <span style={{ color }}>{data}</span>;
  };

  const renderEngineStatus = (isEngineOn: boolean) => {
    return (
      <Tag
        style={{
          fontWeight: 'bold',
          color: isEngineOn ? '#1e9b24' : '#b81414',
          backgroundColor: isEngineOn ? '#95f39a' : '#ffb4b4',
          border: 'none',
          lineHeight: 1.4,
          fontSize: 12,
        }}
      >
        {isEngineOn ? 'ON' : 'OFF'}
      </Tag>
    );
  };

  const inFlowConvert = (inFlow: number) => {
    if (literUnit === 'Liter/Min') {
      return inFlow > 0 ? inFlow / 60 : 0;
    } else {
      return inFlow;
    }
  };

  function generateDefaultData(data: any) {
    return data.reduce((acc: any, item: any) => {
      if (Array.isArray(item.children)) {
        item.children.forEach((child: any) => {
          if (child.dataIndex) {
            acc[child.dataIndex] = '-';
          }
        });
      }

      if (item.dataIndex) {
        acc[item.dataIndex] = '-';
      }

      return acc;
    }, {});
  }

  function convertAE(arr: string[]) {
    return arr.map((uniqueId) => {
      const [, prefix, number, direction] = uniqueId.split('-');

      const mappedDirection =
        direction === '1' ? 'In' : direction === '2' ? 'Out' : '';

      if (direction) {
        return `${prefix}${number}${mappedDirection}`;
      } else {
        return `${prefix}${number}`;
      }
    });
  }

  function convertFlowmeter(arr: string[]) {
    return arr.map((uniqueId) => {
      const [, prefix, number, direction] = uniqueId.split('-');
      let position = ''

      if (number === '1') {
        position = 'Port'
      } else if (number === '2') {
        position = 'Starboard'
      } else if (number === '3') {
        position = 'Center'
      }

      const mappedDirection =
        direction === '1' ? 'In' : direction === '2' ? 'Out' : '';

      if (direction) {
        return `fm${position}${mappedDirection}`;
      } else {
        return `-`;
      }
    });
  }

  const dataSource = useMemo(() => {
    if (logs && Object.keys(logs).length) {
      let intervalInSecond = convertIntervalToSecond(filter.interval);

      const containerTimestamp = generateTimestampData({
        startTime: filter.startAt,
        endTime: filter.endAt,
        interval: filter.interval,
        intervalInSecond,
        timezone,
      });

      // FM Device Config
      const fmDevices = asset.devices?.filter((item: any) =>
        /fm/i.test(item.uniqueId.toLowerCase()),
      );

      const fmColumns = columnsFiltered.filter((col: any) => {
        return col.key?.match(/fm/i);
      });

      let fmKeys: any[] = [];

      if (fmDevices && fmDevices.length) {
        fmKeys = convertFlowmeter(fmDevices.map((item) => item.uniqueId));
      }

      // AE Device Config
      const aeDevices = asset.devices?.filter((item: any) =>
        /ae/i.test(item.uniqueId.toLowerCase()),
      );
      const aeColumns = columnsFiltered.filter((col: any) => {
        return col.key?.match(/ae/i);
      });

      let aeKeys: any[] = [];

      if (aeDevices && aeDevices.length) {
        aeKeys = convertAE(aeDevices.map((item) => item.uniqueId));
      }

      let fm: any;
      let rpm: any;
      let clutch: any;
      let ae: any;
      let gps;
      let fmResult: any = {};
      let rpmResult: any = {};
      let clutchResult: any = {};
      let aeResult: any = {};
      let gpsResult: any = {};

      let meFuelCons = 0;
      let aeFuelCons = 0;
      const defaultClutch = {
        port_Clutch: '-',
        port_ClutchStatus: '-',
        port_ClutchOperationMode: '-',
        port_ClutchRunningTime: '-',
        starboard_Clutch: '-',
        starboard_ClutchStatus: '-',
        starboard_ClutchOperationMode: '-',
        starboard_ClutchRunningTime: '-',
        center_Clutch: '-',
        center_ClutchStatus: '-',
        center_ClutchOperationMode: '-',
        center_ClutchRunningTime: '-',
      };

      const flowmeterLookup = createBucketLookup(logs.flowmeter);
      const rpmLookup = createBucketLookup(logs.rpm);
      const clutchLookup = createBucketLookup(logs.clutch);
      const aeLookup = createBucketLookup(logs.ae);
      const gpsLookup = createBucketLookup(logs.gps);
      const operationModeLookup = createBucketLookup(logs.operation_mode);
      const formatBucketDate = createBucketDateFormatter(
        filter.interval,
        timezone,
      );

      const results = containerTimestamp.map((timestamp) => {
        if (logs.flowmeter) {
          const fmBucket = flowmeterLookup.get(timestamp);

          if (fmBucket && fmKeys.length) {
            fm = fmBucket;
            meFuelCons = (() => {
              const keys = Object.keys(fm).filter(
                (k) =>
                  ![
                    'totalFuelCons',
                    'totalFuelConsVolume',
                    'totalFuelConsumption',
                    'totalRunningTime',
                  ].includes(k),
              );

              const processed = new Set<string>();

              return keys.reduce((sum, k) => {
                const base = k.replace(/(In|Out)$/, '');
                if (processed.has(base)) return sum;
                processed.add(base);

                // liter of the bucket, the same whatever the flow unit shown
                return sum + (getBucketFuelCons(fm, base, filter.interval) || 0);
              }, 0);
            })();

            const getFlowData = (obj: any) => {
              const inFlow =
                filter.interval === 'minute'
                  ? inFlowConvert(obj.volumeFlowrate)
                  : inFlowConvert(obj.stats?.avgVolumeFlowrate);
              return {
                runningTime: obj.stats?.sumRunningTime,
                volumeInventory: obj.volumeInventory,
                inFlow,
                temperature: obj.temperature,
                density: obj.density,
              };
            };

            const assignFlowmeterData = (
              prefix: string,
              fuelCons: number | undefined,
              flowData?: ReturnType<typeof getFlowData>,
              dataTypeVal?: any,
              prefixSingleFlow?: string,
            ) => {
              const result: Record<string, React.ReactNode> = {
                [`${prefixSingleFlow ? 'fm' + prefixSingleFlow : prefix}_FuelConsumption`]: showTemp(
                  convertData(fuelCons),
                  dataTypeVal,
                ),
              };

              if (flowData) {
                result[`${prefix}_VolumeTotal`] = showTemp(
                  convertData(flowData.volumeInventory),
                  dataTypeVal,
                );
                result[`${prefix}_InFlow`] = showTemp(
                  convertData(flowData.inFlow, false, flowData.inFlow < 0),
                  dataTypeVal,
                );
                result[`${prefix}_Temperature`] = showTemp(
                  convertData(flowData.temperature),
                  dataTypeVal,
                );
                result[`${prefix}_InDensity`] = showTemp(
                  convertData(flowData.density),
                  dataTypeVal,
                );
              }

              return result;
            };

            const processed = new Set<string>();

            fmResult = fmKeys.reduce(
              (acc: Record<string, React.ReactNode | null>, k) => {
                let base = k.replace(/(In|Out)$/, '');

                if (base) {
                  base = base.slice(2).replace(/^[A-Z]/, (char: any) => char.toLowerCase())
                }

                if (processed.has(base)) return acc;
                processed.add(base);

                const inObj = fm[`${base}In`];
                const outObj = fm[`${base}Out`];
                const statObj = fm[base];

                const dataType =
                  inObj?.dataType ||
                  outObj?.dataType ||
                  statObj?.dataType ||
                  null;
                const baseUpp = base.charAt(0).toUpperCase() + base.slice(1).toLowerCase()
                const fuelCons = getBucketFuelCons(fm, base, filter.interval);

                if (inObj && outObj) {
                  const flowDataIn = getFlowData(inObj);
                  const flowDataOut = getFlowData(outObj);

                  acc = {
                    ...acc,
                    ...assignFlowmeterData(
                      `fm${baseUpp}In`,
                      fuelCons,
                      flowDataIn,
                      dataType,
                    ),
                    ...assignFlowmeterData(
                      `fm${baseUpp}Out`,
                      fuelCons,
                      flowDataOut,
                      dataType,
                    ),
                    [`fm${baseUpp}_FuelConsumption`]: showTemp(
                      convertData(fuelCons),
                      dataType,
                    ),
                  };
                } else if (inObj) {
                  const flowData = getFlowData(inObj);
                  acc = {
                    ...acc,
                    ...assignFlowmeterData(
                      `fm${baseUpp}In`,
                      fuelCons,
                      flowData,
                      dataType,
                      baseUpp,
                    ),
                  };
                } else if (outObj) {
                  const flowData = getFlowData(outObj);
                  acc = {
                    ...acc,
                    ...assignFlowmeterData(
                      `fm${baseUpp}Out`,
                      fuelCons,
                      flowData,
                      dataType,
                      baseUpp,
                    ),
                  };
                } else if (statObj) {
                  acc = {
                    ...acc,
                    ...assignFlowmeterData(
                      `fm${baseUpp}`,
                      fuelCons,
                      undefined,
                      dataType,
                    ),
                  };
                }

                return acc;
              },
              generateDefaultData(fmColumns) as Record<
                string,
                React.ReactNode | null
              >,
            );
          } else {
            const defaultFlowmeterCustom = generateDefaultData(fmColumns);
            fmResult = { ...defaultFlowmeterCustom };
            meFuelCons = 0;
          }
        }

        if (logs.rpm) {
          const rpmBucket = rpmLookup.get(timestamp);
          if (rpmBucket) {
            rpm = rpmBucket;
            let portOpsMode = '';
            let starboardOpsMode = '';
            let centerOpsMode = '';

            if (isOperationMode && rpm) {
              const calculatedOperationMode = operationModeLookup.get(timestamp);
              if (calculatedOperationMode) {
                portOpsMode = calculatedOperationMode.port?.mode || '-';
                starboardOpsMode =
                  calculatedOperationMode.starboard?.mode || '-';
                centerOpsMode = calculatedOperationMode.center?.mode || '-';
              } else if (operationModes.length) {
                const foundPortOpsMode = operationModes.find(
                  (item) => item.deviceId === (rpm as any).port?.deviceUniqueId,
                );
                const foundStarboardOpsMode = operationModes.find(
                  (item) =>
                    item.deviceId === (rpm as any).starboard?.deviceUniqueId,
                );
                const foundCenterOpsMode = operationModes.find(
                  (item) =>
                    item.deviceId === (rpm as any).center?.deviceUniqueId,
                );

                if (foundPortOpsMode && foundPortOpsMode.operationModeDetails) {
                  const foundPortRPMMax =
                    foundPortOpsMode.operationModeDetails.find((item) => {
                      const threshold = String(
                        foundPortOpsMode.deviceId || '',
                      )
                        .toLowerCase()
                        .includes('clutch')
                        ? item.clutchMax
                        : item.rpmMax;
                      if (
                        threshold === null ||
                        threshold === undefined ||
                        threshold === ''
                      ) {
                        return false;
                      }
                      return Number(threshold) >= rpm.port?.rpm;
                    });
                  if (foundPortRPMMax) {
                    portOpsMode = foundPortRPMMax.name;
                  }
                }

                if (
                  foundStarboardOpsMode &&
                  foundStarboardOpsMode.operationModeDetails
                ) {
                  const foundStarboardRPMMax =
                    foundStarboardOpsMode.operationModeDetails.find((item) => {
                      const threshold = String(
                        foundStarboardOpsMode.deviceId || '',
                      )
                        .toLowerCase()
                        .includes('clutch')
                        ? item.clutchMax
                        : item.rpmMax;
                      if (
                        threshold === null ||
                        threshold === undefined ||
                        threshold === ''
                      ) {
                        return false;
                      }
                      return Number(threshold) >= rpm.starboard?.rpm;
                    });
                  if (foundStarboardRPMMax) {
                    starboardOpsMode = foundStarboardRPMMax.name;
                  }
                }

                if (
                  foundCenterOpsMode &&
                  foundCenterOpsMode.operationModeDetails
                ) {
                  const foundCenterRPMMax =
                    foundCenterOpsMode.operationModeDetails.find((item) => {
                      const threshold = String(
                        foundCenterOpsMode.deviceId || '',
                      )
                        .toLowerCase()
                        .includes('clutch')
                        ? item.clutchMax
                        : item.rpmMax;
                      if (
                        threshold === null ||
                        threshold === undefined ||
                        threshold === ''
                      ) {
                        return false;
                      }
                      return Number(threshold) >= rpm.center?.rpm;
                    });
                  if (foundCenterRPMMax) {
                    centerOpsMode = foundCenterRPMMax.name;
                  }
                }
              }
            }

            rpmResult = {
              // port
              port_Rpm: showTemp(
                convertData(rpm?.port?.rpm),
                rpm?.port?.dataType,
              ),
              port_EngineStatus: renderEngineStatus(rpm?.port?.rpm > 0),
              port_RunningTime: showTemp(
                toHHMMSS(rpm?.port?.runningTime),
                rpm?.port?.dataType,
              ),
              port_OperationMode: portOpsMode || '-',

              // starboard
              starboard_Rpm: showTemp(
                convertData(rpm?.starboard?.rpm),
                rpm?.starboardIn?.dataType,
              ),
              starboard_EngineStatus: renderEngineStatus(
                rpm?.starboard?.rpm > 0,
              ),
              starboard_RunningTime: showTemp(
                toHHMMSS(rpm?.starboard?.runningTime),
                rpm?.starboardIn?.dataType,
              ),
              starboard_OperationMode: starboardOpsMode || '-',
            };

            if (rpm.center) {
              rpmResult = {
                ...rpmResult,
                // center
                center_Rpm: showTemp(
                  convertData(rpm?.center?.rpm),
                  rpm?.centerIn?.dataType,
                ),
                center_EngineStatus: renderEngineStatus(rpm?.center?.rpm > 0),
                center_RunningTime: showTemp(
                  toHHMMSS(rpm?.center?.runningTime),
                  rpm?.centerIn?.dataType,
                ),
                center_OperationMode: centerOpsMode || '-',
              };
            } else {
              rpmResult = {
                ...rpmResult,
                center_Rpm: '-',
                center_EngineStatus: '-',
                center_RunningTime: '-',
              };
            }
          } else {
            rpmResult = { ...defaultRPM };
          }
        }

        clutchResult = { ...defaultClutch };

        if (logs.clutch) {
          const clutchBucket = clutchLookup.get(timestamp);

          if (clutchBucket) {
            clutch = clutchBucket;
            const calculatedOperationMode = isOperationMode
              ? operationModeLookup.get(timestamp)
              : undefined;
            const clutchStatus = (value?: number | null) =>
              typeof value === 'number'
                ? renderEngineStatus(value > 0)
                : '-';

            clutchResult = {
              port_Clutch: showTemp(
                convertData(clutch?.port?.clutch),
                clutch?.port?.dataType,
              ),
              port_ClutchStatus: clutchStatus(clutch?.port?.clutch),
              port_ClutchOperationMode:
                calculatedOperationMode?.clutchPort?.mode || '-',
              port_ClutchRunningTime: showTemp(
                toHHMMSS(clutch?.port?.runningTime),
                clutch?.port?.dataType,
              ),
              starboard_Clutch: showTemp(
                convertData(clutch?.starboard?.clutch),
                clutch?.starboard?.dataType,
              ),
              starboard_ClutchStatus: clutchStatus(clutch?.starboard?.clutch),
              starboard_ClutchOperationMode:
                calculatedOperationMode?.clutchStarboard?.mode || '-',
              starboard_ClutchRunningTime: showTemp(
                toHHMMSS(clutch?.starboard?.runningTime),
                clutch?.starboard?.dataType,
              ),
              center_Clutch: showTemp(
                convertData(clutch?.center?.clutch),
                clutch?.center?.dataType,
              ),
              center_ClutchStatus: clutchStatus(clutch?.center?.clutch),
              center_ClutchOperationMode:
                calculatedOperationMode?.clutchCenter?.mode || '-',
              center_ClutchRunningTime: showTemp(
                toHHMMSS(clutch?.center?.runningTime),
                clutch?.center?.dataType,
              ),
            };
          }
        }

        if (logs.ae) {
          const aeBucket = aeLookup.get(timestamp);

          if (aeBucket && aeKeys.length) {
            ae = aeBucket;
            aeFuelCons = (() => {
              const keys = Object.keys(ae).filter(
                (k) =>
                  ![
                    'totalFuelCons',
                    'totalFuelConsVolume',
                    'totalFuelConsumption',
                    'totalRunningTime',
                  ].includes(k),
              );

              const processed = new Set<string>();

              return keys.reduce((sum, k) => {
                const base = k.replace(/(In|Out)$/, '');
                if (processed.has(base)) return sum;
                processed.add(base);

                // liter of the bucket, the same whatever the flow unit shown
                return sum + (getBucketFuelCons(ae, base, filter.interval) || 0);
              }, 0);
            })();

            const getFlowData = (obj: any) => {
              const inFlow =
                filter.interval === 'minute'
                  ? inFlowConvert(obj.volumeFlowrate)
                  : inFlowConvert(obj.stats?.avgVolumeFlowrate);
              return {
                runningTime: obj.stats?.sumRunningTime,
                volumeInventory: obj.volumeInventory,
                inFlow,
                temperature: obj.temperature,
                density: obj.density,
              };
            };

            const assignAEData = (
              prefix: string,
              runningTime: number | undefined,
              fuelCons: number | undefined,
              flowData?: ReturnType<typeof getFlowData>,
              dataTypeVal?: any,
              prefixSingleFlow?: string,
            ) => {
              const result: Record<string, React.ReactNode> = {
                [`${prefix}_RunningTime`]: showTemp(
                  toHHMMSS(runningTime),
                  dataTypeVal,
                ),
                [`${prefixSingleFlow || prefix}_FuelConsumption`]: showTemp(
                  convertData(fuelCons),
                  dataTypeVal,
                ),
              };

              if (flowData) {
                result[`${prefix}_VolumeTotal`] = showTemp(
                  convertData(flowData.volumeInventory),
                  dataTypeVal,
                );
                result[`${prefix}_InFlow`] = showTemp(
                  convertData(flowData.inFlow, false, flowData.inFlow < 0),
                  dataTypeVal,
                );
                result[`${prefix}_Temperature`] = showTemp(
                  convertData(flowData.temperature),
                  dataTypeVal,
                );
                result[`${prefix}_InDensity`] = showTemp(
                  convertData(flowData.density),
                  dataTypeVal,
                );
              }

              return result;
            };

            const processed = new Set<string>();

            aeResult = aeKeys.reduce(
              (acc: Record<string, React.ReactNode | null>, k) => {
                const base = k.replace(/(In|Out)$/, '');

                if (processed.has(base)) return acc;
                processed.add(base);

                const inObj = ae[`${base}In`];
                const outObj = ae[`${base}Out`];
                const statObj = ae[base];

                const dataType =
                  inObj?.dataType ||
                  outObj?.dataType ||
                  statObj?.dataType ||
                  null;

                const fuelCons = getBucketFuelCons(ae, base, filter.interval);

                if (inObj && outObj) {
                  const flowDataIn = getFlowData(inObj);
                  const flowDataOut = getFlowData(outObj);

                  acc = {
                    ...acc,
                    ...assignAEData(
                      `${base}In`,
                      flowDataIn.runningTime,
                      fuelCons,
                      flowDataIn,
                      dataType,
                    ),
                    ...assignAEData(
                      `${base}Out`,
                      flowDataOut.runningTime,
                      fuelCons,
                      flowDataOut,
                      dataType,
                    ),
                    [`${base}_FuelConsumption`]: showTemp(
                      convertData(fuelCons),
                      dataType,
                    ),
                  };
                } else if (inObj) {
                  const flowData = getFlowData(inObj);
                  acc = {
                    ...acc,
                    ...assignAEData(
                      `${base}In`,
                      flowData.runningTime,
                      fuelCons,
                      flowData,
                      dataType,
                      base,
                    ),
                  };
                } else if (outObj) {
                  const flowData = getFlowData(outObj);
                  acc = {
                    ...acc,
                    ...assignAEData(
                      `${base}Out`,
                      flowData.runningTime,
                      fuelCons,
                      flowData,
                      dataType,
                      base,
                    ),
                  };
                } else if (statObj) {
                  acc = {
                    ...acc,
                    ...assignAEData(
                      base,
                      statObj.stats?.sumRunningTime,
                      fuelCons,
                      undefined,
                      dataType,
                    ),
                  };
                }

                return acc;
              },
              generateDefaultData(aeColumns) as Record<
                string,
                React.ReactNode | null
              >,
            );
          } else {
            const defaultAECustom = generateDefaultData(aeColumns);
            aeResult = { ...defaultAECustom };
            aeFuelCons = 0;
          }
        }

        if (logs.gps) {
          const gpsBucket = gpsLookup.get(timestamp);
          if (gpsBucket) {
            gps = gpsBucket;
            let gps_speed = undefined;
            let gps_distance = undefined;

            if (speedUnit === 'KNOT' && gps?.speed) {
              gps_speed = toKnot(gps.speed);
            } else {
              gps_speed = gps?.speed;
            }

            if (distanceUnit === 'KM' && gps?.stats?.sumDistance) {
              gps_distance = toKM(gps?.stats?.sumDistance);
            } else if (distanceUnit === 'NM' && gps?.stats?.sumDistance) {
              gps_distance = toNM(gps?.stats?.sumDistance);
            } else if (distanceUnit === 'MILES' && gps?.stats?.sumDistance) {
              gps_distance = toMiles(gps?.stats?.sumDistance);
            } else {
              gps_distance = gps?.stats?.sumDistance;
            }

            gpsResult = {
              gps_coordinate:
                gps?.latitude && gps?.longitude
                  ? `${latToDMS(gps?.latitude)}, ${longToDMS(gps?.longitude)}`
                  : '-',
              gps_latitude: showTemp(gps?.latitude, gps?.dataType),
              gps_longitude: showTemp(gps?.longitude, gps?.dataType),
              gps_heading: showTemp(gps?.heading, gps?.dataType),
              gps_speed: showTemp(convertData(gps_speed), gps?.dataType),
              gps_distance: showTemp(convertData(gps_distance), gps?.dataType),
            };
          } else {
            gpsResult = { ...defaultGPS };
          }
        }
        let totalFuelCons = meFuelCons + aeFuelCons;

        // console.log('aeFuelCons', {totalFuelCons, meFuelCons, aeFuelCons})

        return {
          ...{...fmResult, meFuelCons: convertData(meFuelCons)},
          ...rpmResult,
          ...clutchResult,
          ...gpsResult,
          ...aeResult,
          timestamp,
          totalFuelCons: convertData(totalFuelCons),
          dateTime: formatBucketDate(timestamp),
        };
      });

      return results;
    }
  }, [
    logs,
    timezone,
    speedUnit,
    distanceUnit,
    literUnit,
    operationModes,
    columnsFiltered,
  ]);

  // console.log('columnsFiltered', {columnsFiltered, dataSource})

  return (
    <React.Fragment>
      {!isReportSchedule && !isMobile && (
        <ReportSummary
          defaultOpenBody={['body1', 'body2', 'body3']}
          devices={asset.devices || []}
          assetType={asset.assetType}
        />
      )}

      {!isReportSchedule && !isMobile && (
        <Divider style={{ margin: '5px 0' }} />
      )}

      {!isReportSchedule && isMobile && (
        <MobileDataLogSummary>
          <MobileDataLogSection title='Summary'>
            <MobileSummaryRow
              label='Total Fuel Cons.'
              value={(
                (summaryData.totalEngineFuelCons || 0) +
                (summaryData.totalAEFuelCons || 0)
              ).toFixed(2)}
              unit='Liter'
            />
            <MobileSummaryRow
              label='Total Cruise'
              value={gpsSummary.totalCruise}
              unit={renderMobileUnitSelect(
                cruiseUnit,
                (value) => {
                  setCruiseUnit(value);
                  setDistanceUnit(value);
                },
                cruiseUnitOptions,
              )}
            />
            <MobileSummaryRow
              label='Average Speed'
              value={gpsSummary.avgSpeed}
              unit={renderMobileUnitSelect(
                speedUnit,
                (value) => setSpeedUnit(value),
                speedUnitOptions,
              )}
            />
            <MobileSummaryRow
              label='Distance'
              value={gpsSummary.totalDistance}
              unit={renderMobileUnitSelect(
                distanceUnit,
                (value) => {
                  setCruiseUnit(value);
                  setDistanceUnit(value);
                },
                distanceUnitOptions,
              )}
            />
          </MobileDataLogSection>

          {asset.assetType !== EAssetType.EMS && (
            <>
              <MobileDataLogSection title='Engine Fuel Consumption'>
                {availableSummaryColumns.columnFlowmeterPort && (
                  <MobileSummaryRow
                    label='Port'
                    value={(summaryData.portTotalFuel || 0).toFixed(2)}
                    unit='Liter'
                  />
                )}
                {availableSummaryColumns.columnFlowmeterStarboard && (
                  <MobileSummaryRow
                    label='Starboard'
                    value={(summaryData.starboardTotalFuel || 0).toFixed(2)}
                    unit='Liter'
                  />
                )}
                {availableSummaryColumns.columnFlowmeterCenter && (
                  <MobileSummaryRow
                    label='Center'
                    value={(summaryData.centerTotalFuel || 0).toFixed(2)}
                    unit='Liter'
                  />
                )}
                <MobileSummaryRow
                  label='Total Fuel Cons.'
                  value={(summaryData.totalEngineFuelCons || 0).toFixed(2)}
                  unit='Liter'
                />
              </MobileDataLogSection>

              <MobileDataLogSection title='Engine Running Time'>
                <MobileSummaryInlineGrid>
                  {availableSummaryColumns.columnRPMPort && (
                    <MobileSummaryRow
                      label='Port'
                      value={toHHMMSS(summaryData.portRunningHour)}
                    />
                  )}
                  {availableSummaryColumns.columnRPMStarboard && (
                    <MobileSummaryRow
                      label='Starboard'
                      value={toHHMMSS(summaryData.starboardRunningHour)}
                    />
                  )}
                  {availableSummaryColumns.columnRPMCenter && (
                    <MobileSummaryRow
                      label='Center'
                      value={toHHMMSS(summaryData.centerRunningHour)}
                    />
                  )}
                </MobileSummaryInlineGrid>
              </MobileDataLogSection>

              <MobileDataLogSection title='Engine Average RPM'>
                {availableSummaryColumns.columnRPMPort && (
                  <MobileSummaryRow
                    label='Port'
                    value={(summaryData.portRPM || 0).toFixed(2)}
                    unit='RPM'
                  />
                )}
                {availableSummaryColumns.columnRPMStarboard && (
                  <MobileSummaryRow
                    label='Starboard'
                    value={(summaryData.starboardRPM || 0).toFixed(2)}
                    unit='RPM'
                  />
                )}
                {availableSummaryColumns.columnRPMCenter && (
                  <MobileSummaryRow
                    label='Center'
                    value={(summaryData.centerRPM || 0).toFixed(2)}
                    unit='RPM'
                  />
                )}
              </MobileDataLogSection>

              <MobileDataLogSection title='Average Flow'>
                {availableSummaryColumns.columnFlowmeterPort && (
                  <MobileSummaryRow
                    label='Port'
                    value={(summaryData.portAverageFlow || 0).toFixed(2)}
                    unit='Liter/H'
                  />
                )}
                {availableSummaryColumns.columnFlowmeterStarboard && (
                  <MobileSummaryRow
                    label='Starboard'
                    value={(summaryData.starboardAverageFlow || 0).toFixed(2)}
                    unit='Liter/H'
                  />
                )}
                {availableSummaryColumns.columnFlowmeterCenter && (
                  <MobileSummaryRow
                    label='Center'
                    value={(summaryData.centerAverageFlow || 0).toFixed(2)}
                    unit='Liter/H'
                  />
                )}
              </MobileDataLogSection>
            </>
          )}

          <MobileDataLogSection title='AE Fuel Consumption'>
            {aeFuelRows.map((row) => (
              <MobileSummaryRow
                key={row.key}
                label={row.label}
                value={row.value}
                unit='Liter'
              />
            ))}
          </MobileDataLogSection>

          <MobileDataLogSection title='AE Running Time'>
            {aeRunningRows.map((row) => (
              <MobileSummaryRow
                key={row.key}
                label={row.label}
                value={row.value}
              />
            ))}
          </MobileDataLogSection>
        </MobileDataLogSummary>
      )}

      {!isReportSchedule && (
        <ContainerFilterTable>
          <Wrapper>
            <TableConfiguration columns={columns} />
          </Wrapper>
          <Wrapper className='mobile-hide-data-log-actions'>
            {isUserHasPermission(['IMPORT_DATA_LOG' + '.CREATE']) && (
              <ImportDataReport />
            )}
          </Wrapper>
        </ContainerFilterTable>
      )}

      <MobileTableFrame>
        <Table
          loading={loading}
          className='table-report-log'
          columns={columnsFiltered}
          dataSource={dataSource}
          size='small'
          bordered
          pagination={isReportSchedule ? false : { pageSize: 500 }}
          components={isReportSchedule || isMobile ? undefined : vt}
          sticky={
            isMobile && !isReportSchedule
              ? {
                  offsetHeader: mobileStickyTableOffset,
                  getContainer: getStickyTableContainer,
                }
              : false
          }
          scroll={
            isReportSchedule || isMobile
              ? { x: 'max-content' }
              : { x: 700, y: windowHeight - 300 }
          }
        />
      </MobileTableFrame>
    </React.Fragment>
  );
};

type MobileAERowType = {
  key: string;
  label: string;
  value: string;
};

function generateMobileAERows(
  source: Record<string, any>,
  type: 'fuelCons' | 'runningTime',
): MobileAERowType[] {
  const keys = Object.keys(source || {}).filter((key) => {
    if (type === 'fuelCons') {
      return /^totalAEFuelCons/i.test(key) || (/ae/i.test(key) && /Fuel/i.test(key));
    }

    return (
      /^totalAERunningTime/i.test(key) ||
      (/ae/i.test(key) && /RunningHour|RunningTime/i.test(key))
    );
  });

  const sortedKeys = keys.sort((a, b) => {
    if (/^totalAE/i.test(a)) return 1;
    if (/^totalAE/i.test(b)) return -1;

    const parse = (value: string) => value.match(/ae(\d+)/i)?.[1] || '999';
    return Number(parse(a)) - Number(parse(b));
  });

  return uniqueByLabel(
    sortedKeys.map((key) => ({
      key,
      label: convertShortAELabel(key)
        .replace('Total AE Fuel Cons', 'Total Fuel Cons.')
        .replace('Total AE Running Time', 'Total Running Time')
        .replace(/AE(\d+)/, 'AE $1'),
    })),
  ).map((row) => ({
    key: row.key,
    label: row.label,
    value:
      type === 'fuelCons'
        ? Number(source[row.key] || 0).toFixed(2)
        : toHHMMSS(source[row.key] || 0),
  }));
}

interface MobileDataLogSectionProps {
  title: string;
  children: React.ReactNode;
}

const MobileDataLogSection: React.FC<MobileDataLogSectionProps> = ({
  title,
  children,
}) => (
  <MobileDataLogSectionContainer>
    <MobileDataLogSectionHeader>
      <MobileDataLogSectionTitle>
        <FileTextOutlined />
        <span>{title}</span>
      </MobileDataLogSectionTitle>
      <MinusOutlined />
    </MobileDataLogSectionHeader>
    <MobileSummaryRows>{children}</MobileSummaryRows>
  </MobileDataLogSectionContainer>
);

interface MobileSummaryRowProps {
  label: string;
  value: React.ReactNode;
  unit?: React.ReactNode;
}

const MobileSummaryRow: React.FC<MobileSummaryRowProps> = ({
  label,
  value,
  unit,
}) => (
  <MobileSummaryItem>
    <MobileSummaryLabel>{label}</MobileSummaryLabel>
    <MobileSummaryValue>
      <span>{value}</span>
      {unit && <MobileSummaryUnit>{unit}</MobileSummaryUnit>}
    </MobileSummaryValue>
  </MobileSummaryItem>
);

const MobileDataLogSummary = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    background: #f4f6f9;
  }
`;

const MobileDataLogSectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 10px 20px 12px;
  border-bottom: 1px solid #d9d9d9;
`;

const MobileDataLogSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 30px;
`;

const MobileDataLogSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #333333;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;

  .anticon {
    flex: 0 0 auto;
    font-size: 16px;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MobileSummaryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const MobileSummaryInlineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
`;

const MobileSummaryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 42px;
  padding: 4px 12px;
  border-radius: 8px;
  background: #e8ebf3;
  color: #1e1e1e;
`;

const MobileSummaryLabel = styled.div`
  min-width: 0;
  overflow: hidden;
  color: #1e1e1e;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileSummaryValue = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  color: #1e1e1e;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.2;
  text-align: right;
  white-space: nowrap;
`;

const MobileSummaryUnit = styled.span`
  display: inline-flex;
  align-items: center;
  color: #1e1e1e;
  font-size: 10px;
  font-weight: 400;
  line-height: 1.2;
`;

const MobileSummarySelect = styled(Select)`
  min-width: 74px;

  .ant-select-selector {
    min-height: 24px !important;
    padding: 0 4px !important;
    border-color: #d9d9d9 !important;
    border-radius: 4px !important;
    background: #ffffff !important;
  }

  .ant-select-selection-item {
    padding-inline-end: 14px !important;
    color: #333333;
    font-size: 12px;
    font-weight: 500;
    line-height: 22px !important;
  }

  .ant-select-arrow {
    right: 4px;
    font-size: 9px;
  }
`;

const ContainerFilterTable = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;

  @media (max-width: 767px) {
    align-items: stretch;
    gap: 8px;
    justify-content: flex-start;
    overflow-x: auto;
    padding: 36px 20px 8px;

    .mobile-hide-data-log-actions {
      display: none;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 767px) {
    flex: 0 0 auto;

    .ant-btn {
      min-height: 36px;
      border-radius: 8px;
      background: #264284;
      font-size: 12px;
      font-weight: 600;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

const MobileTableFrame = styled.div`
  @media (max-width: 767px) {
    margin: 0 12px;
    border: 0;
    border-radius: 0;
    background: #ffffff;

    .ant-table {
      font-size: 12px;
    }

    .ant-table-thead > tr > th {
      white-space: nowrap;
      background: #ffffff;
      color: #1e1e1e;
      font-size: 12px;
      font-weight: 600;
      text-align: center;
    }

    .ant-table-sticky-holder {
      box-shadow: 0 2px 8px rgba(30, 30, 30, 0.08);
    }

    .ant-table-tbody > tr > td {
      white-space: nowrap;
      font-size: 12px;
      text-align: center;
    }

    .ant-pagination {
      margin: 12px;
    }
  }
`;

export const TitleChild = styled.div`
  display: flex;
  flex-direction: row;
  text-align: center;
  justify-content: center;
  gap: 5px;
`;

export const Unit = styled.div`
  font-size: 12px;
  font-weight: bold;
`;

export default TableReportLog;
